import React from 'react';
import { ActivityIndicator, Modal, StyleSheet, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { COLORS } from '../profileStyles';
import type { Translate } from '../profileTypes';

interface LogoutConfirmModalProps {
  visible: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: Translate;
}

export function LogoutConfirmModal({ visible, loading, onCancel, onConfirm, t }: LogoutConfirmModalProps) {
  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={() => {
        if (!loading) onCancel();
      }}
    >
      <View style={logoutConfirmStyles.backdrop}>
        <View style={logoutConfirmStyles.card}>
          <View style={logoutConfirmStyles.iconWrap}>
            <MaterialIcons name="logout" size={24} color={COLORS.error} />
          </View>
          <Text style={logoutConfirmStyles.title}>{t('auth.logout.title')}</Text>
          <Text style={logoutConfirmStyles.copy}>{t('auth.logout.message')}</Text>

          <View style={logoutConfirmStyles.actions}>
            <TouchableOpacity
              style={[logoutConfirmStyles.button, logoutConfirmStyles.cancelButton]}
              disabled={loading}
              onPress={onCancel}
              accessibilityRole="button"
            >
              <Text style={logoutConfirmStyles.cancelText}>{t('auth.logout.cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              testID="logout_confirm"
              style={[logoutConfirmStyles.button, logoutConfirmStyles.confirmButton, loading && logoutConfirmStyles.disabledButton]}
              disabled={loading}
              onPress={onConfirm}
              accessibilityRole="button"
            >
              {loading ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={logoutConfirmStyles.confirmText}>{t('auth.logout.confirm')}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const logoutConfirmStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backgroundColor: 'rgba(17, 24, 39, 0.36)',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 24,
    shadowColor: '#111827',
    shadowOpacity: 0.14,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 14 },
    elevation: 10,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    backgroundColor: '#FFF1F2',
  },
  title: {
    color: reviewDesk.ink,
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: 0,
    marginBottom: 8,
  },
  copy: {
    color: reviewDesk.muted,
    fontSize: 16,
    lineHeight: 23,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  button: {
    minHeight: 48,
    flex: 1,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  confirmButton: {
    backgroundColor: COLORS.error,
  },
  disabledButton: {
    opacity: 0.72,
  },
  cancelText: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
  },
  confirmText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
});
